import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Result,
  Segmented,
  Select,
  Skeleton,
  Spin,
  Table,
  Tooltip,
  notification,
} from 'antd';
import {
  BankOutlined,
  BugOutlined,
  CheckCircleFilled,
  ExclamationCircleOutlined,
  FileProtectOutlined,
  IdcardOutlined,
  MedicineBoxOutlined,
  PauseCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import lib from '../../assets/utils/lib';
import { usePermissions } from '../../assets/utils/usePermissions';
import './test-subjects.css';

dayjs.extend(relativeTime);

/**
 * Test subjects (TEST-SUBJECTS, spec "Test Subjects — Exclusion Spec").
 *
 * Organisations, policies, enrollees, facilities and staff marked here go
 * through every flow normally (check-in, PA, claims, payment), but nothing they
 * touch counts toward Aman's operational aggregates (platform dashboards,
 * totals, analytics, reports, exports, audiences). A facility's or an
 * organisation's view of itself is never filtered.
 *
 * Reading: admin.* | admin.system_config | test_subjects.read | test_subjects.manage.
 * Marking, unmarking and the switch: admin.* | test_subjects.manage — the API
 * checks this strictly.
 */

export const SUBJECT_TYPES = [
  { value: 'policy', label: 'Policy', plural: 'Policies', icon: <FileProtectOutlined />, help: 'Principal and every dependant, incl. later ones.' },
  { value: 'organisation', label: 'Organisation', plural: 'Organisations', icon: <BankOutlined />, help: 'Every policy and enrollee under it.' },
  { value: 'enrollee', label: 'Enrollee', plural: 'Enrollees', icon: <UserOutlined />, help: 'One person, e.g. a test dependant.' },
  { value: 'facility', label: 'Facility', plural: 'Facilities', icon: <MedicineBoxOutlined />, help: 'Everything at the facility.' },
  { value: 'facility_user', label: 'Facility staff', plural: 'Facility staff', icon: <TeamOutlined />, help: 'Out of staff activity and audiences.' },
  { value: 'admin_user', label: 'Admin staff', plural: 'Admin staff', icon: <IdcardOutlined />, help: 'Out of staff activity and audiences.' },
];
const TYPE = Object.fromEntries(SUBJECT_TYPES.map((t) => [t.value, t]));

const QUICK_REASONS = ['QA / release testing', 'Demo account', 'Internal staff test', 'Training'];

const ok = (resp) => resp && !resp?.data?.error;

const TypeBadge = ({ type }) => (
  <span className={`ts-badge ts-tone-${type}`} aria-hidden>
    {TYPE[type]?.icon || <BugOutlined />}
  </span>
);

const When = ({ at, by }) => {
  if (!at) return null;
  const d = dayjs(at);
  return (
    <Tooltip title={d.isValid() ? d.format('D MMM YYYY, HH:mm') : at}>
      <div className="ts-when">{d.isValid() ? d.fromNow() : at}</div>
      {by ? <div className="ts-when__by">{by}</div> : null}
    </Tooltip>
  );
};

const EmptyState = ({ icon, title, text, action }) => (
  <div className="ts-empty">
    <div className="ts-empty__icon">{icon}</div>
    <div className="ts-empty__title">{title}</div>
    <p className="ts-empty__text">{text}</p>
    {action}
  </div>
);

function ReasonModal({ open, title, okText, danger, description, quick, onCancel, onSubmit }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      okButtonProps={{ danger, disabled: reason.trim().length < 5, loading: busy }}
      onCancel={onCancel}
      onOk={async () => {
        setBusy(true);
        try {
          await onSubmit(reason.trim());
        } finally {
          setBusy(false);
        }
      }}
      destroyOnClose
    >
      {description ? <p className="ts-status__text" style={{ marginBottom: 12 }}>{description}</p> : null}
      {quick ? (
        <div className="ts-reasons">
          {quick.map((r) => (
            <button key={r} type="button" onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
      ) : null}
      <Input.TextArea
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason — at least 5 characters, kept in the audit log"
        maxLength={500}
        showCount
        autoFocus
      />
    </Modal>
  );
}

function MarkModal({ open, preset, onCancel, onDone }) {
  const [type, setType] = useState('policy');
  const [key, setKey] = useState(null);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setType(preset?.type || 'policy');
    setKey(preset?.key || null);
    setOptions(
      preset?.key
        ? [{ value: preset.key, name: preset.label || preset.key, meta: preset.key, label: preset.label || preset.key }]
        : [],
    );
    setImpact(null);
    setReason(preset?.reason || '');
  }, [open, preset]);

  const search = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) return;
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await lib.searchTestSubjectCandidates(type, q.trim());
        const rows = resp?.data?.data || [];
        setOptions(
          rows.map((r) => ({
            value: r.key,
            disabled: r.marked,
            name: r.label,
            meta: [r.key, r.meta, r.marked ? 'already test' : null].filter(Boolean).join(' · '),
            label: r.label,
          })),
        );
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  useEffect(() => {
    let live = true;
    if (!open || !key) {
      setImpact(null);
      return undefined;
    }
    setImpactLoading(true);
    lib
      .getTestSubjectImpact(type, key)
      .then((resp) => {
        if (live) setImpact(ok(resp) ? resp?.data?.data : null);
      })
      .finally(() => live && setImpactLoading(false));
    return () => {
      live = false;
    };
  }, [open, type, key]);

  const covers = impact?.covers || {};
  const stats = [
    ['policies', 'Policies'],
    ['enrollees', 'Enrollees'],
    ['encounters', 'Encounters'],
    ['claims', 'Claims'],
  ].filter(([k]) => covers[k] !== null && covers[k] !== undefined);

  return (
    <Modal
      open={open}
      title="Mark as test"
      okText="Mark as test"
      okButtonProps={{ disabled: !key || reason.trim().length < 5 || impact?.already_marked, loading: busy }}
      onCancel={onCancel}
      onOk={async () => {
        setBusy(true);
        try {
          const resp = await lib.markTestSubject({ type, key, reason: reason.trim() });
          if (ok(resp)) {
            notification.success({ message: resp?.data?.message || 'Marked as test' });
            onDone();
          }
        } finally {
          setBusy(false);
        }
      }}
      destroyOnClose
      width={640}
    >
      <div className="ts-step">
        <div className="ts-step__label">
          <span className="ts-step__num">1</span>What kind of record?
        </div>
        <div className="ts-types" role="radiogroup">
          {SUBJECT_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={type === t.value}
              className={`ts-type ts-tone-${t.value}${type === t.value ? ' is-active' : ''}`}
              onClick={() => {
                if (t.value === type) return;
                setType(t.value);
                setKey(null);
                setOptions([]);
              }}
            >
              <TypeBadge type={t.value} />
              <span>
                <div className="ts-type__name">{t.label}</div>
                <div className="ts-type__help">{t.help}</div>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="ts-step">
        <div className="ts-step__label">
          <span className="ts-step__num">2</span>Find it
        </div>
        <Select
          showSearch
          style={{ width: '100%' }}
          size="large"
          value={key}
          placeholder={`Search ${TYPE[type]?.plural.toLowerCase()} by name, number or email`}
          suffixIcon={<SearchOutlined />}
          filterOption={false}
          onSearch={search}
          onChange={setKey}
          notFoundContent={searching ? <Spin size="small" /> : null}
          optionLabelProp="label"
          options={options}
          optionRender={(o) => (
            <div className="ts-option">
              <span className="ts-option__name">{o.data.name}</span>
              <span className="ts-option__meta">{o.data.meta}</span>
            </div>
          )}
        />
        {key ? (
          impactLoading ? (
            <Skeleton active paragraph={{ rows: 1 }} title={false} style={{ marginTop: 10 }} />
          ) : impact?.already_marked ? (
            <div className="ts-impact">
              <div className="ts-impact__note">This record is already marked as test.</div>
            </div>
          ) : stats.length ? (
            <div className="ts-impact">
              <div className="ts-impact__stats" style={{ gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))` }}>
                {stats.map(([k, label]) => (
                  <div key={k} className="ts-impact__stat">
                    <div className="ts-impact__value">{Number(covers[k]).toLocaleString()}</div>
                    <div className="ts-impact__label">{label} covered</div>
                  </div>
                ))}
              </div>
              <div className="ts-impact__note">
                All of it keeps going through every flow, payment included. It only leaves Aman&apos;s operational
                figures, reports, exports and audiences.
              </div>
            </div>
          ) : null
        ) : null}
      </div>

      <div className="ts-step" style={{ marginBottom: 0 }}>
        <div className="ts-step__label">
          <span className="ts-step__num">3</span>Why is it test?
        </div>
        <div className="ts-reasons">
          {QUICK_REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(r)}>
              {r}
            </button>
          ))}
        </div>
        <Input.TextArea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. QA policy used for release testing"
          maxLength={500}
        />
      </div>
    </Modal>
  );
}

const TestSubjectsPage = () => {
  const { hasAnyPermission } = usePermissions();
  const canRead = useMemo(
    () => hasAnyPermission(['admin.*', 'admin.system_config', 'test_subjects.read', 'test_subjects.manage']),
    [hasAnyPermission],
  );
  const canManage = useMemo(() => hasAnyPermission(['admin.*', 'test_subjects.manage']), [hasAnyPermission]);

  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('active');
  const [typeFilter, setTypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [sugLoading, setSugLoading] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [markPreset, setMarkPreset] = useState(null);
  const [unmarkRow, setUnmarkRow] = useState(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkMarkOpen, setBulkMarkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadSettings = useCallback(async () => {
    const resp = await lib.getTestSubjectSettings();
    if (ok(resp)) setSettings(resp?.data?.data || null);
  }, []);

  const loadList = useCallback(async () => {
    if (tab === 'review') return;
    setLoading(true);
    try {
      const resp = await lib.getTestSubjects({
        status: tab === 'history' ? 'past' : 'active',
        type: typeFilter || undefined,
        q: q || undefined,
        page,
        pageSize: 50,
      });
      if (ok(resp)) setList(resp?.data?.data || { items: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [tab, typeFilter, q, page]);

  const loadSuggestions = useCallback(async () => {
    setSugLoading(true);
    try {
      const resp = await lib.getTestSubjectSuggestions();
      if (ok(resp)) setSuggestions(resp?.data?.data?.items || []);
    } finally {
      setSugLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    loadSettings();
    loadSuggestions();
  }, [canRead, loadSettings, loadSuggestions]);

  useEffect(() => {
    if (canRead && tab !== 'review') loadList();
  }, [canRead, tab, loadList]);

  const refreshAll = () => {
    loadSettings();
    loadSuggestions();
    if (tab !== 'review') loadList();
  };

  const openMark = (preset = null) => {
    setMarkPreset(preset);
    setMarkOpen(true);
  };

  const runLegacyMigration = async () => {
    const dry = await lib.migrateLegacyTestSubjects(false);
    if (!ok(dry)) return;
    const actions = dry?.data?.data?.actions || [];
    const todo = actions.filter((a) => !a.already_marked);
    if (!todo.length) {
      Modal.info({
        title: 'Nothing to migrate',
        content: actions.length
          ? 'The legacy "testing" group is already marked.'
          : 'No policy carries the legacy social_no "testing".',
      });
      return;
    }
    Modal.confirm({
      title: 'Mark the legacy "testing" group?',
      icon: <ExclamationCircleOutlined />,
      content: todo.map((a) => (
        <p key={a.key} style={{ margin: '8px 0 0' }}>
          {a.label} — covers {a.covers_policies} polic{a.covers_policies === 1 ? 'y' : 'ies'}, and any added later.
        </p>
      )),
      okText: 'Mark it',
      onOk: async () => {
        const resp = await lib.migrateLegacyTestSubjects(true);
        if (ok(resp)) {
          notification.success({ message: 'Legacy "testing" group marked as test' });
          refreshAll();
        }
      },
    });
  };

  const selectedRows = (suggestions || []).filter((r) => selected.includes(`${r.type}:${r.key}`));

  // One request per record, in order; what succeeded leaves the list, what failed stays selected.
  const runBulk = async (rows, call, doneMessage) => {
    setBulkBusy(true);
    const done = [];
    try {
      for (const r of rows) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await call(r);
        if (ok(resp)) done.push(`${r.type}:${r.key}`);
      }
    } finally {
      setBulkBusy(false);
    }
    setSuggestions((prev) => (prev || []).filter((r) => !done.includes(`${r.type}:${r.key}`)));
    setSelected((prev) => prev.filter((k) => !done.includes(k)));
    if (done.length) notification.success({ message: `${done.length} ${doneMessage}` });
    if (done.length < rows.length) {
      notification.warning({ message: `${rows.length - done.length} could not be updated and are still selected` });
    }
    loadSettings();
  };

  if (!canRead) {
    return <Result status="403" title="Admin only" subTitle="Viewing test subjects requires admin access." />;
  }

  const on = Boolean(settings?.effective);
  const counts = settings?.active_counts || {};
  const totalMarked = Object.values(counts).reduce((a, b) => a + Number(b || 0), 0);
  const toReview = suggestions?.length || 0;

  const subjectCell = (type, name, key) => (
    <div className={`ts-subject ts-tone-${type}`}>
      <TypeBadge type={type} />
      <div className="ts-subject__text">
        <div className="ts-subject__name">{name || key}</div>
        <div className="ts-subject__meta">
          {TYPE[type]?.label || type}
          <span className="ts-subject__dot">·</span>
          <code className="ts-subject__key">{key}</code>
        </div>
      </div>
    </div>
  );

  const markColumns = [
    {
      title: 'Record',
      key: 'subject',
      render: (_, r) => subjectCell(r.subject_type, r.subject_label, r.subject_key),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      render: (v, r) => (
        <div className="ts-reason">
          {v} {r.source === 'migration' ? <span className="ts-chip">migrated</span> : null}
          {r.unmark_reason ? <div className="ts-reason__sub">Unmarked: {r.unmark_reason}</div> : null}
        </div>
      ),
    },
    {
      title: tab === 'history' ? 'Unmarked' : 'Marked',
      key: 'when',
      width: 190,
      render: (_, r) =>
        tab === 'history' ? (
          <When at={r.unmarked_at} by={r.unmarked_by_name} />
        ) : (
          <When at={r.marked_at} by={r.marked_by_name} />
        ),
    },
    ...(canManage && tab === 'active'
      ? [
          {
            title: '',
            key: 'actions',
            width: 96,
            align: 'right',
            render: (_, r) => (
              <Button size="small" type="text" danger onClick={() => setUnmarkRow(r)}>
                Unmark
              </Button>
            ),
          },
        ]
      : []),
  ];

  const sugColumns = [
    { title: 'Record', key: 'rec', render: (_, r) => subjectCell(r.type, r.label, r.key) },
    { title: 'Old rule matched', dataIndex: 'matched', width: 220, render: (v) => <span className="ts-chip">{v}</span> },
    ...(canManage
      ? [
          {
            title: '',
            key: 'actions',
            width: 200,
            align: 'right',
            render: (_, r) => (
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <Button
                  size="small"
                  onClick={async () => {
                    const resp = await lib.dismissTestSubjectSuggestion(r.type, r.key);
                    if (ok(resp)) {
                      setSuggestions((prev) => (prev || []).filter((x) => !(x.type === r.type && x.key === r.key)));
                    }
                  }}
                >
                  Not test
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={() =>
                    openMark({ type: r.type, key: r.key, label: r.label, reason: 'Confirmed test during legacy-rule review' })
                  }
                >
                  Mark as test
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  const filteredType = typeFilter ? TYPE[typeFilter] : null;

  return (
    <div className="ts-page">
      <header className="ts-masthead">
        <div>
          <h1 className="ts-masthead__title">Test subjects</h1>
          <p className="ts-masthead__meta">
            Marked records go through every flow as normal — check-in, PA, claims, payment — but stay out of
            Aman&apos;s operational figures, reports, exports and audiences. Facilities and organisations still see
            their own figures.
          </p>
        </div>
        <div className="ts-masthead__actions">
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={refreshAll} aria-label="Refresh" />
          </Tooltip>
          {canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openMark()}>
              Mark as test
            </Button>
          ) : null}
        </div>
      </header>

      {settings && settings.schema_ready === false ? (
        <div className="ts-status">
          <span className="ts-status__icon"><ExclamationCircleOutlined /></span>
          <div className="ts-status__body">
            <div className="ts-status__title">Not set up on this server yet</div>
            <p className="ts-status__text">
              Run <code>sqls/migrations/20261012_test_subjects.sql</code>. The first mark creates the tables
              automatically; until then nothing is excluded.
            </p>
          </div>
        </div>
      ) : (
        <section className={`ts-status${on ? ' is-on' : ''}`}>
          <span className="ts-status__icon">{on ? <CheckCircleFilled /> : <PauseCircleOutlined />}</span>
          <div className="ts-status__body">
            <div className="ts-status__title">
              Exclusion
              <span className="ts-status__pill">{settings ? (on ? 'On' : 'Off') : '…'}</span>
              {settings?.env_override ? <span className="ts-chip">server override: {settings.env_override}</span> : null}
            </div>
            <p className="ts-status__text">
              {on
                ? 'Marked records are left out of every operational figure, report, export and audience.'
                : 'Marks are recorded and labelled, but no figure changes yet. Dashboards still apply the old name rule.'}
            </p>
            {!on && toReview > 0 ? (
              <div className="ts-status__hint">
                <ExclamationCircleOutlined />
                {toReview} record{toReview === 1 ? '' : 's'} the old rule hid still need a decision before you turn
                this on.
                <button type="button" onClick={() => setTab('review')}>Review</button>
              </div>
            ) : null}
          </div>
          {canManage ? (
            <div className="ts-status__action">
              <Button type={on ? 'default' : 'primary'} danger={on} onClick={() => setSwitchOpen(true)} disabled={!settings}>
                {on ? 'Turn off' : 'Turn on'}
              </Button>
            </div>
          ) : null}
        </section>
      )}

      <div className="ts-tiles">
        {SUBJECT_TYPES.map((t) => {
          const n = Number(counts[t.value] || 0);
          const active = typeFilter === t.value;
          return (
            <button
              key={t.value}
              type="button"
              className={`ts-tile ts-tone-${t.value}${active ? ' is-active' : ''}${n ? '' : ' is-empty'}`}
              aria-pressed={active}
              onClick={() => {
                setTypeFilter(active ? '' : t.value);
                setPage(1);
                if (tab === 'review') setTab('active');
              }}
            >
              <span className="ts-tile__head">
                <span className="ts-tile__count">{settings ? n : '–'}</span>
                <TypeBadge type={t.value} />
              </span>
              <span className="ts-tile__label">{t.plural} marked</span>
            </button>
          );
        })}
      </div>

      <section className="ts-card">
        <div className="ts-card__bar">
          <Segmented
            value={tab}
            onChange={(k) => {
              setTab(k);
              setPage(1);
            }}
            options={[
              { value: 'active', label: `Marked${totalMarked ? ` · ${totalMarked}` : ''}` },
              { value: 'review', label: `To review${toReview ? ` · ${toReview}` : ''}` },
              { value: 'history', label: 'History' },
            ]}
          />
          {tab !== 'review' ? (
            <div className="ts-card__tools">
              {filteredType ? (
                <span className={`ts-filter-chip ts-tone-${filteredType.value}`}>
                  {filteredType.plural}
                  <button type="button" aria-label="Clear type filter" onClick={() => setTypeFilter('')}>
                    ×
                  </button>
                </span>
              ) : null}
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: 'var(--app-text-soft)' }} />}
                placeholder="Search marked records"
                style={{ width: 260 }}
                onPressEnter={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                onChange={(e) => {
                  if (!e.target.value) {
                    setQ('');
                    setPage(1);
                  }
                }}
              />
            </div>
          ) : null}
        </div>

        {tab === 'review' ? (
          <>
            <div className="ts-review-note">
              <span>
                <strong>Records the retired name rule treated as test.</strong> Mark the real test records; choose
                “Not test” for real people, such as someone named Testimony.
              </span>
              {canManage ? <Button onClick={runLegacyMigration}>Mark the legacy “testing” group</Button> : null}
            </div>
            {canManage && selected.length ? (
              <div className="ts-bulk">
                <span>
                  <strong>{selected.length}</strong> selected
                  <button type="button" className="ts-bulk__clear" onClick={() => setSelected([])}>
                    Clear
                  </button>
                </span>
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  <Button
                    loading={bulkBusy}
                    onClick={() =>
                      runBulk(selectedRows, (r) => lib.dismissTestSubjectSuggestion(r.type, r.key), 'marked as not test')
                    }
                  >
                    Not test
                  </Button>
                  <Button type="primary" loading={bulkBusy} onClick={() => setBulkMarkOpen(true)}>
                    Mark {selected.length} as test
                  </Button>
                </span>
              </div>
            ) : null}
            <Table
              className="ts-table"
              rowKey={(r) => `${r.type}:${r.key}`}
              rowSelection={
                canManage
                  ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys.map(String)) }
                  : undefined
              }
              loading={sugLoading}
              columns={sugColumns}
              dataSource={suggestions || []}
              pagination={{ pageSize: 25, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <EmptyState
                    icon={<CheckCircleFilled />}
                    title="Nothing left to review"
                    text="Every record the old rule caught has a decision."
                  />
                ),
              }}
            />
          </>
        ) : (
          <Table
            className="ts-table"
            rowKey="id"
            loading={loading}
            columns={markColumns}
            dataSource={list.items}
            pagination={{
              current: page,
              pageSize: 50,
              total: list.total,
              hideOnSinglePage: true,
              onChange: setPage,
            }}
            locale={{
              emptyText:
                tab === 'history' ? (
                  <EmptyState icon={<ReloadOutlined />} title="No history yet" text="Records you unmark show up here, with who unmarked them and why." />
                ) : q || typeFilter ? (
                  <EmptyState icon={<SearchOutlined />} title="No matches" text="Nothing marked matches this search or filter." />
                ) : (
                  <EmptyState
                    icon={<BugOutlined />}
                    title="Nothing is marked as test yet"
                    text="Mark the policies, organisations and facilities you test with. Start with the review list: it holds what the old name rule used to hide."
                    action={
                      canManage ? (
                        <span style={{ display: 'inline-flex', gap: 8 }}>
                          <Button onClick={() => setTab('review')}>Open review list</Button>
                          <Button type="primary" icon={<PlusOutlined />} onClick={() => openMark()}>
                            Mark as test
                          </Button>
                        </span>
                      ) : null
                    }
                  />
                ),
            }}
          />
        )}
      </section>

      <MarkModal
        open={markOpen}
        preset={markPreset}
        onCancel={() => setMarkOpen(false)}
        onDone={() => {
          setMarkOpen(false);
          if (markPreset) {
            setSuggestions((prev) => (prev || []).filter((x) => !(x.type === markPreset.type && x.key === markPreset.key)));
          }
          refreshAll();
        }}
      />

      <ReasonModal
        open={bulkMarkOpen}
        title={`Mark ${selected.length} record${selected.length === 1 ? '' : 's'} as test?`}
        okText="Mark as test"
        description="Each keeps going through every flow, payment included, but leaves Aman's operational figures, reports, exports and audiences. Policies and organisations cover everyone under them."
        quick={QUICK_REASONS}
        onCancel={() => setBulkMarkOpen(false)}
        onSubmit={async (reason) => {
          setBulkMarkOpen(false);
          await runBulk(selectedRows, (r) => lib.markTestSubject({ type: r.type, key: r.key, reason }), 'marked as test');
        }}
      />

      <ReasonModal
        open={Boolean(unmarkRow)}
        title={`Unmark ${unmarkRow?.subject_label || unmarkRow?.subject_key || ''}?`}
        okText="Unmark"
        danger
        description="Its activity, past and future, counts in every operational figure again."
        onCancel={() => setUnmarkRow(null)}
        onSubmit={async (reason) => {
          const resp = await lib.unmarkTestSubject(unmarkRow.id, reason);
          if (ok(resp)) {
            notification.success({ message: 'Unmarked' });
            setUnmarkRow(null);
            refreshAll();
          }
        }}
      />

      <ReasonModal
        open={switchOpen}
        title={on ? 'Turn exclusion off?' : 'Turn exclusion on?'}
        okText={on ? 'Turn off' : 'Turn on'}
        danger={on}
        description={
          on
            ? 'Every operational figure will include test activity again, and the dashboards go back to the old name rule.'
            : 'Operational figures, reports, exports and audiences will leave out marked records. The old name rule is retired: anything it hid that is not marked here will count.'
        }
        onCancel={() => setSwitchOpen(false)}
        onSubmit={async (reason) => {
          const resp = await lib.updateTestSubjectSettings({ exclusion_enabled: !on, reason });
          if (ok(resp)) {
            notification.success({ message: on ? 'Exclusion turned off' : 'Exclusion turned on' });
            setSwitchOpen(false);
            loadSettings();
          }
        }}
      />
    </div>
  );
};

export default TestSubjectsPage;
